import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { BaseFormList } from 'projects/admin/src/app/classes/base-form-list';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormModelModel } from 'projects/core/src/lib/models/form-model.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { UserModel } from 'projects/core/src/lib/models/user.model';

class FormModeratorModel extends UserModel {
  public control: UntypedFormControl = new UntypedFormControl('');
}
class FormTemplateModel extends FormModelModel {
  public getPrioModTooltip(): string {
    const result: Array<string> = [];
    const priomod: any = this.controls['priomod'].control.value;
    if (priomod) {
      this.model.users?.forEach((user: UserModel) => {
        if (priomod[user.id]) {
          result.push(user.fullname + ': ' + priomod[user.id]);
        }
      });
    }
    return result.join('<br>');
  }
}

interface MonthEntry {
  month: string;
  label: string;
  limit: UntypedFormControl | null;
  booked: number;
  isPast: boolean;
  originalLimit: number | null;
}

interface OpenedMonthlyEntry {
  templateName: string;
  futureMonths: MonthEntry[];
  pastMonths: MonthEntry[];
  templateId: number;
}

@Component({
  selector: 'app-settings-templates',
  templateUrl: './settings-templates.component.html',
  styleUrls: ['./settings-templates.component.scss']
})
export class SettingsTemplatesComponent extends BaseFormList implements OnInit {

  public models: Array<FormTemplateModel> = [];
  public openedTemplate: FormTemplateModel = null;
  public moderatorsOpened = false;
  public moderators: Array<FormModeratorModel> = [];
  public openedMonthlyEntry: OpenedMonthlyEntry | null = null;
  public showPastMonths = false;

  protected rightLoad = 'admin.planning-settings.show-settings';

  private monthlyLimitsMap = new Map<number, { [month: string]: number | null }>();
  private monthlyLimitsSnapshot = new Map<number, { [month: string]: number | null }>();
  private bookingCountsMap = new Map<number, { [month: string]: number }>();

  constructor(
    protected router: Router,
    protected ui: UiService,
    protected helper: HelperService,
    protected api: ApiService,
    protected auth: AuthStateService,
    protected fb: UntypedFormBuilder,
    public transloco: TranslocoService
  ) {
    super(router, ui, helper, transloco, auth, fb);
  }

  ngOnInit(): void {
    this.loadList();
  }

  public loadList(): void {
    this.loading = true;
    this.api.getTemplateSettings('templates').subscribe({
      next: (result: any) => {
        this.formConfig = result.data.config;
        this.initWithData(result.data.templates);
        this.setOptions(result.data.options);
    },
      error: (error: any) => {

      }
    }).add(() => {
      this.loading = false;
    });
  }

  public onEdit(): void {
    this.monthlyLimitsSnapshot = new Map(this.monthlyLimitsMap);
    super.onEdit();
  }

  public onCancel(): void {
    this.monthlyLimitsMap = new Map(this.monthlyLimitsSnapshot);
    super.onCancel();
  }

  public onSubmit(): void {
    if (this.isValid()) {
      this.ui.hideMessage();
      this.submitting = true;
      const payload: any = {};
      this.models.forEach((formTemplate: FormTemplateModel) => {
        payload[formTemplate.model.id] = {
          ...this.getPayload(formTemplate),
          monthly_limits: this.monthlyLimitsMap.get(formTemplate.model.id) ?? {},
        };
      });
      this.api.saveTemplateSettings('templates', payload).subscribe({
        next: (result: any) => {
          this.inEdit = false;
          this.disableControls();
          this.initWithData(result.data);
          this.ui.toastSuccess(this.transloco.translate('common.messages.settings_saved'), this.transloco.translate('common.saved'));
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          this.setBackendErrors(error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.markAsDirty();
    }
  }

  public editPrioMod(model: FormTemplateModel): void {
    const data = model.controls['priomod'].control.value;
    this.setModerators(model.model.users);
    if (data !== null) {
      this.moderators.forEach((moderator: FormModeratorModel) => {
        if (data[moderator.id]) {
          moderator.control.setValue(data[moderator.id]);
        }
      });
    }
    this.openedTemplate = model;
    this.moderatorsOpened = true;
  }

  public hideModerators(): void {
    this.moderatorsOpened = false;
    this.openedTemplate = null;
    this.clearModeratorPrio();
  }

  public hasMonthlyLimits(formTemplate: FormTemplateModel): boolean {
    const limits = this.monthlyLimitsMap.get(formTemplate.model.id) ?? {};
    return Object.keys(limits).length > 0;
  }

  public openMonthlyLimitsDialog(formTemplate: FormTemplateModel): void {
    const templateId = formTemplate.model.id;
    const storedLimits = this.monthlyLimitsMap.get(templateId) ?? {};
    const bookingCounts = this.bookingCountsMap.get(templateId) ?? {};

    const now = new Date();
    const futureMonths: MonthEntry[] = [];
    const pastMonths: MonthEntry[] = [];

    // Build current + next 11 months
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const month = this.formatYearMonth(d);
      const label = d.toLocaleDateString(this.transloco.getActiveLang(), { month: 'long', year: 'numeric' });
      const originalLimit = storedLimits[month] != null ? Number(storedLimits[month]) : null;
      futureMonths.push({
        month,
        label,
        limit: new UntypedFormControl(originalLimit),
        booked: bookingCounts[month] ?? 0,
        isPast: false,
        originalLimit,
      });
    }

    // Build past months (only those with a stored limit)
    const allMonths = new Set<string>([
      ...Object.keys(storedLimits),
      ...Object.keys(bookingCounts),
    ]);
    allMonths.forEach((month) => {
      if (month >= this.formatYearMonth(now)) return;
      const booked = bookingCounts[month] ?? 0;
      const originalLimit = storedLimits[month] != null ? Number(storedLimits[month]) : null;
      if (originalLimit === null) return;
      const parts = month.split('-');
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
      const label = d.toLocaleDateString(this.transloco.getActiveLang(), { month: 'long', year: 'numeric' });
      pastMonths.push({ month, label, limit: null, booked, isPast: true, originalLimit });
    });
    pastMonths.sort((a, b) => b.month.localeCompare(a.month));

    this.showPastMonths = false;
    this.openedMonthlyEntry = {
      templateName: formTemplate.model.name,
      futureMonths,
      pastMonths,
      templateId,
    };
  }

  public clampLimitToBooked(entry: MonthEntry): void {
    if (entry.limit === null) return;
    const val = entry.limit.value;
    if (val !== null && val < entry.booked) {
      entry.limit.setValue(entry.booked);
    }
  }

  public saveMonthlyLimitsDialog(): void {
    if (!this.openedMonthlyEntry) return;

    const limits: { [month: string]: number | null } = {};
    this.openedMonthlyEntry.futureMonths.forEach((entry) => {
      this.clampLimitToBooked(entry);
      const val = entry.limit.value;
      if (val !== null && val !== '') {
        limits[entry.month] = Number(val);
      }
    });

    this.monthlyLimitsMap.set(this.openedMonthlyEntry.templateId, limits);
    this.openedMonthlyEntry = null;
  }

  public closeMonthlyLimitsDialog(): void {
    this.openedMonthlyEntry = null;
  }

  public saveModerators(): void {
    let data: { [key: string]: number | null } | null = null;
    this.moderators.forEach((moderator: FormModeratorModel) => {
      const value = moderator.control.value;
      if (value > 0) {
        if (data === null) {
          data = {};
        }
        data[moderator.id] = value;
      }
    });
    this.openedTemplate.controls['priomod'].control.setValue(data);
    this.hideModerators();
  }

  private clearModeratorPrio(): void {
    this.setModerators([]);
    this.moderators.forEach((moderator: FormModeratorModel) => {
      moderator.control.setValue('');
    });
  }

  private setModerators(moderators: Array<any>) {
    this.moderators = moderators.map((moderator: any) => {
      return new FormModeratorModel().deserialize(moderator);
    });
  }

  private formatYearMonth(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  private initWithData(templates: any): void {
    this.clearControls();
    this.clearModels();
    this.monthlyLimitsMap.clear();
    this.bookingCountsMap.clear();
    templates.forEach((data: any) => {
      const template = new TemplateModel().deserialize(data);
      this.monthlyLimitsMap.set(template.id, template.settings?.monthly_limits ?? {});
      this.bookingCountsMap.set(template.id, data.booking_counts ?? {});
      const formTemplate = new FormTemplateModel(template);
      this.addControls(formTemplate.initForm(this.formConfig, template.settings));
      this.models.push(formTemplate);
    });
  }

}
